/**
 * Info Page
 * Displays application information, version, licenses, and credits
 * Styled to match R4S Macula design
 */

import { useState } from 'react';
import { Layout } from '../components/layout';
import {
  Info,
  Code,
  Server,
  Database,
  Globe,
  Shield,
  ExternalLink,
  Heart,
  Scale,
  ChevronDown,
  ChevronUp,
  Brain,
  Zap,
  BarChart3,
  GitBranch,
  Lock,
} from 'lucide-react';

// App version info
const APP_INFO = {
  name: 'R4S OmniMens',
  fullName: 'AI Production Planner & ERP Visualisation Platform',
  version: '1.0.0',
  buildDate: '2026-02-27',
  environment: import.meta.env.MODE || 'development',
  apiVersion: 'v1',
};

const TECH_STACK = [
  { name: 'React', version: '19.x', icon: Code, color: 'text-blue-500' },
  { name: 'TypeScript', version: '5.x', icon: Code, color: 'text-blue-600' },
  { name: 'Django', version: '5.x', icon: Server, color: 'text-green-600' },
  { name: 'PostgreSQL', version: '15.x', icon: Database, color: 'text-blue-700' },
  { name: 'Tailwind CSS', version: '4.x', icon: Code, color: 'text-cyan-500' },
  { name: 'Redis', version: '7.x', icon: Database, color: 'text-red-500' },
  { name: 'Celery', version: '5.x', icon: Zap, color: 'text-green-500' },
  { name: 'Claude AI', version: 'Sonnet 4', icon: Brain, color: 'text-purple-500' },
];

const FEATURES = [
  'AI-Powered Production Planning (Claude API)',
  'OData Sync with Infor LN ERP',
  'Interactive Gantt Charts & Dashboards',
  'APS Scheduling Engine (Forward/Backward)',
  'Real-Time WebSocket Updates',
  'Scenario Comparison & Approval Workflow',
  'KPI Dashboard (OEE, OTD, Utilization)',
  'Multi-Site Support & Cross-Site Sync',
  'Write-Back to Infor LN (OData PATCH)',
  'Role-Based Access Control (JWT)',
];

// License information
const APP_LICENSE = {
  type: 'Proprietary',
  owner: 'Roket4Solutions, LDA',
  restrictions: [
    { label: 'No Distribution', desc: 'This software may not be redistributed or shared' },
    { label: 'No Reverse Engineering', desc: 'Decompilation, disassembly, or reverse engineering is prohibited' },
    { label: 'No Modification', desc: 'Unauthorized modification of the software is prohibited' },
    { label: 'Confidential', desc: 'All aspects of the software are confidential and proprietary' },
    { label: 'Commercial Use', desc: 'Licensed only for authorized users and organizations' },
  ],
};

interface Dependency {
  name: string;
  version: string;
  license: string;
  url: string;
}

const PYTHON_DEPS: Dependency[] = [
  { name: 'Django', version: '5.x', license: 'BSD-3-Clause', url: 'https://www.djangoproject.com/' },
  { name: 'Django REST Framework', version: '3.14+', license: 'BSD-2-Clause', url: 'https://www.django-rest-framework.org/' },
  { name: 'djangorestframework-simplejwt', version: '5.3+', license: 'MIT', url: 'https://github.com/jazzband/djangorestframework-simplejwt' },
  { name: 'django-cors-headers', version: '4.0+', license: 'MIT', url: 'https://github.com/adamchainz/django-cors-headers' },
  { name: 'django-filter', version: '23.0+', license: 'BSD-2-Clause', url: 'https://django-filter.readthedocs.io/' },
  { name: 'drf-spectacular', version: '0.27+', license: 'BSD-3-Clause', url: 'https://drf-spectacular.readthedocs.io/' },
  { name: 'Celery', version: '5.3+', license: 'BSD-3-Clause', url: 'https://docs.celeryq.dev/' },
  { name: 'Redis (Python)', version: '4.5+', license: 'MIT', url: 'https://github.com/redis/redis-py' },
  { name: 'Channels', version: '4.0+', license: 'BSD-3-Clause', url: 'https://channels.readthedocs.io/' },
  { name: 'Daphne', version: '4.0+', license: 'BSD-3-Clause', url: 'https://github.com/django/daphne' },
  { name: 'Anthropic SDK', version: '0.42+', license: 'MIT', url: 'https://github.com/anthropics/anthropic-sdk-python' },
  { name: 'httpx', version: '0.25+', license: 'BSD-3-Clause', url: 'https://www.python-httpx.org/' },
  { name: 'psycopg2', version: '2.9+', license: 'LGPL-3.0', url: 'https://www.psycopg.org/' },
  { name: 'cryptography', version: '41.0+', license: 'Apache-2.0 / BSD-3-Clause', url: 'https://cryptography.io/' },
  { name: 'WhiteNoise', version: '6.6+', license: 'MIT', url: 'https://whitenoise.readthedocs.io/' },
  { name: 'django-jazzmin', version: '3.0+', license: 'MIT', url: 'https://django-jazzmin.readthedocs.io/' },
  { name: 'django-import-export', version: '4.0+', license: 'BSD-2-Clause', url: 'https://django-import-export.readthedocs.io/' },
  { name: 'Gunicorn', version: '21.0+', license: 'MIT', url: 'https://gunicorn.org/' },
];

const JS_DEPS: Dependency[] = [
  { name: 'React', version: '19.x', license: 'MIT', url: 'https://react.dev/' },
  { name: 'React Router', version: '7.x', license: 'MIT', url: 'https://reactrouter.com/' },
  { name: 'TanStack React Query', version: '5.x', license: 'MIT', url: 'https://tanstack.com/query' },
  { name: 'Axios', version: '1.x', license: 'MIT', url: 'https://axios-http.com/' },
  { name: 'Recharts', version: '3.x', license: 'MIT', url: 'https://recharts.org/' },
  { name: 'Lucide React', version: '0.563+', license: 'ISC', url: 'https://lucide.dev/' },
  { name: 'Tailwind CSS', version: '4.x', license: 'MIT', url: 'https://tailwindcss.com/' },
  { name: 'Vite', version: '7.x', license: 'MIT', url: 'https://vitejs.dev/' },
  { name: 'TypeScript', version: '5.9+', license: 'Apache-2.0', url: 'https://www.typescriptlang.org/' },
  { name: 'SheetJS (xlsx)', version: '0.18+', license: 'Apache-2.0', url: 'https://sheetjs.com/' },
  { name: 'React Grid Layout', version: '2.x', license: 'MIT', url: 'https://github.com/react-grid-layout/react-grid-layout' },
];

function DependencyTable({ deps, title }: { deps: Dependency[]; title: string }) {
  return (
    <div className="mb-6">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">{title}</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 pr-4 text-gray-500 font-medium">Library</th>
              <th className="text-left py-2 pr-4 text-gray-500 font-medium">Version</th>
              <th className="text-left py-2 text-gray-500 font-medium">License</th>
            </tr>
          </thead>
          <tbody>
            {deps.map((dep) => (
              <tr key={dep.name} className="border-b border-gray-100">
                <td className="py-2 pr-4">
                  <a
                    href={dep.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#45bdd6] hover:underline"
                  >
                    {dep.name}
                  </a>
                </td>
                <td className="py-2 pr-4 text-gray-600">{dep.version}</td>
                <td className="py-2">
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                    {dep.license}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function InfoPage() {
  const [showLicenses, setShowLicenses] = useState(false);

  return (
    <Layout title="Info">
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 bg-gradient-to-br from-[#45bdd6] to-[#674EA7] rounded-2xl flex items-center justify-center shadow-lg">
              <svg
                viewBox="0 0 100 100"
                className="w-16 h-16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M50 15L75 30V60L50 75L25 60V30L50 15Z"
                  stroke="white"
                  strokeWidth="3"
                  fill="none"
                />
                <path
                  d="M50 35L62 42V58L50 65L38 58V42L50 35Z"
                  fill="white"
                />
                <circle cx="50" cy="50" r="8" fill="#45bdd6" />
              </svg>
            </div>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {APP_INFO.name}
          </h1>
          <p className="text-lg text-gray-500 mb-4">
            {APP_INFO.fullName}
          </p>
          <div className="flex items-center justify-center gap-3">
            <span className="px-3 py-1 bg-[#45bdd6]/10 text-[#45bdd6] rounded-full text-sm font-medium">
              Version {APP_INFO.version}
            </span>
            <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
              {APP_INFO.environment}
            </span>
          </div>
        </div>

        {/* Info Cards Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* About Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-[#45bdd6]/10 rounded-lg">
                <Info className="w-5 h-5 text-[#45bdd6]" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">About</h2>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed">
              R4S OmniMens is an AI-powered production planning and ERP
              visualization platform. It connects to Infor LN via OData APIs,
              uses Claude AI for intelligent scheduling optimization, and
              provides interactive dashboards, Gantt charts, and KPI monitoring
              for manufacturing operations.
            </p>
          </div>

          {/* Build Info Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-[#674EA7]/10 rounded-lg">
                <Server className="w-5 h-5 text-[#674EA7]" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Build Info</h2>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Version</span>
                <span className="text-gray-900 font-medium">{APP_INFO.version}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Build Date</span>
                <span className="text-gray-900 font-medium">{APP_INFO.buildDate}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">API Version</span>
                <span className="text-gray-900 font-medium">{APP_INFO.apiVersion}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Environment</span>
                <span className="text-gray-900 font-medium capitalize">{APP_INFO.environment}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Features Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <Shield className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Features</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {FEATURES.map((feature, index) => (
              <div key={index} className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-[#45bdd6] rounded-full mt-2 flex-shrink-0" />
                <span className="text-sm text-gray-600">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Tech Stack Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Code className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Technology Stack</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            {TECH_STACK.map((tech) => (
              <div
                key={tech.name}
                className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg"
              >
                <tech.icon className={`w-4 h-4 ${tech.color}`} />
                <span className="text-sm font-medium text-gray-700">{tech.name}</span>
                <span className="text-xs text-gray-400">{tech.version}</span>
              </div>
            ))}
          </div>
        </div>

        {/* License & Third-Party Dependencies */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <button
            onClick={() => setShowLicenses(!showLicenses)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Scale className="w-5 h-5 text-amber-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Licenses & Third-Party Dependencies</h2>
            </div>
            {showLicenses ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {showLicenses && (
            <div className="mt-6">
              {/* Application License */}
              <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="text-base font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Lock className="w-4 h-4 text-gray-700" />
                  R4S OmniMens Application License
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  <strong>R4S OmniMens</strong> is proprietary software owned by <strong>{APP_LICENSE.owner}</strong>.
                  The application itself is <strong>NOT</strong> open source and is protected by copyright law.
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  <strong>All Rights Reserved.</strong> This software and its source code are confidential and
                  proprietary to {APP_LICENSE.owner}. Any unauthorized copying, distribution, modification,
                  reverse engineering, or use of this software is strictly prohibited.
                </p>
                <div className="space-y-2">
                  {APP_LICENSE.restrictions.map((r) => (
                    <div key={r.label} className="flex items-start gap-2 text-sm">
                      <span className="font-medium text-gray-700 min-w-[160px]">{r.label}:</span>
                      <span className="text-gray-500">{r.desc}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Third-Party Notice */}
              <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Legal Notice:</strong> The following section lists only the third-party open source
                  libraries that R4S OmniMens uses internally. These libraries retain their original licenses,
                  but their inclusion does not make R4S OmniMens itself open source.
                </p>
              </div>

              {/* Backend Dependencies */}
              <DependencyTable deps={PYTHON_DEPS} title="Python Dependencies (Backend)" />

              {/* Frontend Dependencies */}
              <DependencyTable deps={JS_DEPS} title="JavaScript Dependencies (Frontend)" />

              {/* Footer */}
              <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between text-xs text-gray-400">
                <span>Last Updated: {APP_INFO.buildDate}</span>
                <span className="flex items-center gap-1">
                  <GitBranch className="w-3 h-3" />
                  Generated for R4S OmniMens
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Company Info */}
        <div className="bg-gradient-to-br from-[#45bdd6]/5 to-[#674EA7]/5 rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <Globe className="w-5 h-5 text-[#45bdd6]" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Developed by</h2>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">
                Roket4Solutions, LDA
              </h3>
              <p className="text-sm text-gray-500">
                Innovative Software Solutions for Enterprise
              </p>
            </div>

            <a
              href="https://www.roket4.solutions"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf] transition-colors text-sm font-medium"
            >
              Visit Website
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-center text-sm text-gray-500 flex items-center justify-center gap-1">
              Made with <Heart className="w-4 h-4 text-red-500 fill-red-500" /> by Roket4Solutions
            </p>
            <p className="text-center text-xs text-gray-400 mt-2">
              © {new Date().getFullYear()} Roket4Solutions, LDA. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
